import { useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';

/* ── small helpers ── */
function Badge({ value }) {
  const num = Number(value);
  const color =
    num >= 75 ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
    num >= 50 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' :
    'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {num}/100
    </span>
  );
}

function InfoRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex gap-3 py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="text-xs text-slate-500 dark:text-slate-400 w-36 flex-shrink-0">{label}</span>
      <span className="text-xs text-slate-800 dark:text-slate-200 break-all">{String(value)}</span>
    </div>
  );
}

function List({ title, items, icon = '•' }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="mt-3">
      <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">{title}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-xs text-slate-600 dark:text-slate-400">
            <span className="mt-0.5 text-violet-500 flex-shrink-0">{icon}</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ApiModeNotice({ mode }) {
  if (mode !== 'llm_only') return null;
  return (
    <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs">
      <span className="flex-shrink-0 mt-0.5">⚠️</span>
      <span>
        <strong>YouTube API key not configured.</strong> Results are based on AI analysis of the video URL alone —
        live view counts, titles, and tags were not fetched. Add a <code>YOUTUBE_API_KEY</code> to your server
        environment for full analysis.
      </span>
    </div>
  );
}

/* ── per-tab result renderers ── */
function AnalyzeResult({ data }) {
  const a = data.analysis || {};
  return (
    <div className="space-y-4">
      <ApiModeNotice mode={data.api_mode} />

      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">SEO Score</span>
        <Badge value={a.score ?? 0} />
      </div>

      {data.title && (
        <InfoRow label="Title" value={data.title} />
      )}
      {data.description_preview && (
        <InfoRow label="Description (preview)" value={data.description_preview} />
      )}
      {data.tag_count != null && (
        <InfoRow label="Tag count" value={data.tag_count} />
      )}

      {data.statistics && (
        <div className="grid grid-cols-3 gap-3 mt-2">
          {[
            ['Views', data.statistics.views?.toLocaleString()],
            ['Likes', data.statistics.likes?.toLocaleString()],
            ['Comments', data.statistics.comments?.toLocaleString()],
          ].map(([label, val]) => (
            <div key={label} className="rounded-lg bg-slate-50 dark:bg-slate-900 p-3 text-center">
              <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100 mt-0.5">{val}</p>
            </div>
          ))}
        </div>
      )}

      {a.title_assessment && <InfoRow label="Title assessment" value={a.title_assessment} />}
      {a.description_assessment && <InfoRow label="Description assessment" value={a.description_assessment} />}
      {a.tags_assessment && <InfoRow label="Tags assessment" value={a.tags_assessment} />}

      <List title="Top issues" items={a.top_issues} icon="⚠" />
      <List title="Recommendations" items={a.recommendations} icon="✓" />
    </div>
  );
}

function OptimizeResult({ data }) {
  const opt = data.optimized_metadata || {};
  return (
    <div className="space-y-4">
      <ApiModeNotice mode={data.api_mode} />

      {data.current_title && <InfoRow label="Current title" value={data.current_title} />}
      {opt.title && (
        <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Optimized title</p>
          <p className="text-sm font-semibold text-green-800 dark:text-green-300">{opt.title}</p>
        </div>
      )}
      {opt.description && (
        <div>
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Optimized description</p>
          <pre className="text-xs whitespace-pre-wrap text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-3 rounded-md max-h-48 overflow-auto">
            {opt.description}
          </pre>
        </div>
      )}
      {opt.thumbnail_text_suggestion && (
        <InfoRow label="Thumbnail text" value={opt.thumbnail_text_suggestion} />
      )}
      <List title="Tags" items={opt.tags} icon="#" />
      <List title="Hashtags" items={opt.hashtags} icon="#" />
    </div>
  );
}

function TranscriptResult({ data }) {
  return (
    <div className="space-y-3">
      <InfoRow label="Video ID" value={data.video_id} />
      <InfoRow label="Segments" value={data.entry_count} />
      {data.transcript && (
        <div>
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Transcript</p>
          <pre className="text-xs whitespace-pre-wrap text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-3 rounded-md max-h-72 overflow-auto">
            {data.transcript}
          </pre>
        </div>
      )}
    </div>
  );
}

function SchemaResult({ data }) {
  const schema = data.json_ld || data.schema;
  return (
    <div className="space-y-3">
      {data.name && <InfoRow label="Video title" value={data.name} />}
      {schema && (
        <div>
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">VideoObject JSON-LD</p>
          <pre className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-3 rounded-md overflow-auto max-h-72">
            {typeof schema === 'string' ? schema : JSON.stringify(schema, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function ApiErrorPanel({ data }) {
  const title = data.error_title;
  const message = data.error_message;
  const hints = Array.isArray(data.hints) ? data.hints.filter(Boolean) : [];
  const detail = data.error_detail;
  const legacy = data.error;
  const structured = Boolean(title);

  const legacyLong = legacy && (legacy.length > 220 || legacy.includes('\n'));

  return (
    <div className="rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/90 dark:bg-red-950/35 p-5 text-red-900 dark:text-red-100 shadow-sm">
      {structured ? (
        <>
          <h3 className="text-sm font-semibold tracking-tight text-red-950 dark:text-red-50 mb-1.5">
            {title}
          </h3>
          {message ? (
            <p className="text-sm text-red-800/95 dark:text-red-200/90 leading-relaxed">{message}</p>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-red-800 dark:text-red-200">
          <span className="font-semibold text-red-900 dark:text-red-100">Error: </span>
          {legacyLong ? `${legacy.slice(0, 200).trim()}…` : legacy || 'Request failed'}
        </p>
      )}

      {hints.length > 0 ? (
        <ul className="mt-3 space-y-1.5 text-xs text-red-800 dark:text-red-300/95 list-disc pl-4 marker:text-red-400">
          {hints.map((h, i) => (
            <li key={i} className="leading-snug">
              {h}
            </li>
          ))}
        </ul>
      ) : null}

      {detail ? (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-medium text-red-700 dark:text-red-400 hover:underline select-none">
            Full message from YouTube
          </summary>
          <pre className="mt-2 text-xs leading-relaxed whitespace-pre-wrap break-words font-mono text-red-900/90 dark:text-red-200/85 bg-red-100/60 dark:bg-black/25 p-3 rounded-lg border border-red-200/70 dark:border-red-900/50 max-h-72 overflow-y-auto">
            {detail}
          </pre>
        </details>
      ) : null}

      {!structured && legacyLong ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-red-700 dark:text-red-400 hover:underline select-none">
            Full error text
          </summary>
          <pre className="mt-2 text-xs leading-relaxed whitespace-pre-wrap break-words font-mono text-red-900/90 dark:text-red-200/85 bg-red-100/60 dark:bg-black/25 p-3 rounded-lg border border-red-200/70 dark:border-red-900/50 max-h-72 overflow-y-auto">
            {legacy}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

function ResultsPanel({ tab, data }) {
  if (!data) return null;

  if (data.success === false) {
    return <ApiErrorPanel data={data} />;
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-4">Results</h3>
      {tab === 'analyze' && <AnalyzeResult data={data} />}
      {tab === 'optimize' && <OptimizeResult data={data} />}
      {tab === 'transcript' && <TranscriptResult data={data} />}
      {tab === 'schema' && <SchemaResult data={data} />}
    </div>
  );
}

/* ── main page ── */
export default function VideoSEOPage() {
  const [activeTab, setActiveTab] = useState('analyze');
  const [videoUrl, setVideoUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const handleAction = async () => {
    if (!videoUrl.trim()) {
      setStatus('Please enter a YouTube video URL.');
      return;
    }
    setLoading(true);
    setStatus('');
    setData(null);
    try {
      const { data: res } = await api.post(`/video-seo/${activeTab}`, {
        video_url: videoUrl,
        target_keywords: keywords ? keywords.split(',').map((k) => k.trim()).filter(Boolean) : [],
      });
      setData(res);
    } catch (error) {
      setStatus(errDetail(error));
    } finally {
      setLoading(false);
    }
  };

  const TABS = [
    { id: 'analyze', label: 'Analyze' },
    { id: 'optimize', label: 'Optimize' },
    { id: 'transcript', label: 'Transcript' },
    { id: 'schema', label: 'Schema' },
  ];

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Video SEO</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
          Analyze, optimize metadata, transcripts, and VideoObject schema.
        </p>
      </div>

      {/* tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-3 text-sm transition-colors ${
              activeTab === t.id
                ? 'border-b-2 border-violet-600 font-medium text-violet-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => { setActiveTab(t.id); setData(null); setStatus(''); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* input card */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            YouTube Video URL
          </label>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
          />
        </div>

        {activeTab === 'optimize' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Target Keywords <span className="text-slate-400 font-normal">(comma separated)</span>
            </label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="seo, digital marketing"
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            />
          </div>
        )}

        <button
          type="button"
          disabled={loading}
          onClick={handleAction}
          className="bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          )}
          {loading ? 'Running…' : `Run ${TABS.find(t => t.id === activeTab)?.label}`}
        </button>

        {status && (
          <div className="p-3 rounded-md text-sm bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {status}
          </div>
        )}
      </div>

      <ResultsPanel tab={activeTab} data={data} />
    </motion.div>
  );
}
